import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export const HomeFeatureSection: React.FC = () => {
    const { t } = useLanguage();
    const sectionRef = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <>
            <section
                ref={sectionRef}
                className="relative w-full min-h-screen bg-transparent text-white flex items-center justify-center p-8 overflow-hidden"
            >
                <div className="max-w-7xl w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center z-10">

                    <div className={`space-y-8 transition-all duration-1000 ml-16 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-3xl lg:text-4xl font-bold leading-tight text-slate-50">
                            {t('feature.title')} <br />
                            <span className="text-slate-50">
                                {t('feature.titleAccent')}
                            </span>
                        </h2>

                        <p className="text-xl text-gray-400 leading-relaxed delay-100 transition-all">
                            {t('feature.desc')}
                        </p>

                        <ul className="space-y-6 text-lg text-gray-300 delay-200 transition-all">
                            <li className="flex items-start gap-4">
                                <span className="mt-2.5 w-2 h-2 rounded-full bg-slate-50 flex-shrink-0" />
                                <span>
                                    <strong className="text-slate-50">{t('feature.item1.title')}</strong> {t('feature.item1.desc')}
                                </span>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="mt-2.5 w-2 h-2 rounded-full bg-slate-50 flex-shrink-0" />
                                <span>
                                    <strong className="text-slate-50">{t('feature.item2.title')}</strong> {t('feature.item2.desc')}
                                </span>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="mt-2.5 w-2 h-2 rounded-full bg-slate-50 flex-shrink-0" />
                                <span>
                                    <strong className="text-slate-50">{t('feature.item3.title')}</strong> {t('feature.item3.desc')}
                                </span>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="mt-2.5 w-2 h-2 rounded-full bg-slate-50 flex-shrink-0" />
                                <span>
                                    <strong className="text-slate-50">{t('feature.item4.title')}</strong> {t('feature.item4.desc')}
                                </span>
                            </li>
                        </ul>

                        <p className="text-sm text-gray-500 italic pt-4 delay-300 transition-all">
                            {t('feature.tagline')}
                        </p>
                    </div>

                    {/* Right Premium Component (Moved from Hero) */}
                    <div
                        className={`relative transition-all duration-1000 delay-300 flex items-center justify-center ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}
                    >
                        <div
                            className="relative pointer-events-none"
                            style={{
                                animation: 'breathe 4s ease-in-out infinite',
                                willChange: 'transform',
                                transform: 'translateZ(0)'
                            }}
                        >
                            {/* 底层 - 灰色毛玻璃背景 */}
                            <div
                                className="absolute -inset-4 rounded-3xl"
                                style={{
                                    background: 'linear-gradient(145deg, rgba(160,160,160,0.2) 0%, rgba(140,140,140,0.15) 50%, rgba(130,130,130,0.2) 100%)',
                                    backdropFilter: 'blur(16px)',
                                    WebkitBackdropFilter: 'blur(16px)',
                                    boxShadow: `
                                    0 24px 48px rgba(0,0,0,0.4),
                                    0 8px 16px rgba(0,0,0,0.2),
                                    inset 0 0.5px 0.5px rgba(255,255,255,0.1),
                                    inset 0 -0.5px 0.5px rgba(0,0,0,0.05),
                                    0 0 0 0.5px rgba(255,255,255,0.05)
                                `
                                }}
                            />

                            {/* 中间层 - 合同照片 (Identical to Hero) */}
                            <div className="relative w-[24vw] h-[30vw] rounded-2xl overflow-hidden shadow-2xl bg-white">
                                <img
                                    src="/测试合同照片.png"
                                    alt="Contract Preview"
                                    className="w-full h-full object-contain"
                                />
                            </div>

                            {/* 顶层 - 液态玻璃容器 */}
                            <div className="absolute -inset-4 rounded-3xl overflow-hidden pointer-events-none">
                                {/* 流动光束动画 */}
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        willChange: 'transform, opacity',
                                        background: `
                                        linear-gradient(
                                            135deg,
                                            transparent 0%,
                                            transparent 20%,
                                            rgba(255, 255, 255, 0.05) 25%,
                                            rgba(255, 255, 255, 0.15) 30%,
                                            rgba(255, 255, 255, 0.2) 35%,
                                            rgba(255, 255, 255, 0.15) 40%,
                                            rgba(255, 255, 255, 0.05) 45%,
                                            transparent 50%,
                                            transparent 100%
                                        )
                                    `,
                                        animation: 'liquidShine 3s ease-in-out infinite',
                                    }}
                                />

                                {/* 第二道流动光束 */}
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: `
                                        linear-gradient(
                                            135deg,
                                            transparent 0%,
                                            transparent 50%,
                                            rgba(255, 255, 255, 0.04) 55%,
                                            rgba(255, 255, 255, 0.1) 60%,
                                            rgba(255, 255, 255, 0.04) 65%,
                                            transparent 70%,
                                            transparent 100%
                                        )
                                    `,
                                        animation: 'liquidShine 3s ease-in-out infinite 1.5s',
                                    }}
                                />

                                {/* 右上角液态高光 */}
                                <div
                                    className="absolute top-0 right-0 w-48 h-48"
                                    style={{
                                        background: 'radial-gradient(ellipse at 90% 10%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 30%, transparent 60%)',
                                        animation: 'liquidPulse 4s ease-in-out infinite',
                                    }}
                                />

                                {/* 边缘高光 */}
                                <div
                                    className="absolute top-0 left-8 right-8 h-[1px]"
                                    style={{
                                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3) 20%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.3) 80%, transparent)'
                                    }}
                                />
                                <div
                                    className="absolute top-8 bottom-8 right-0 w-[1px]"
                                    style={{
                                        background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 80%, transparent)'
                                    }}
                                />

                                {/* 内边框 */}
                                <div
                                    className="absolute inset-0 rounded-3xl"
                                    style={{
                                        boxShadow: `
                                        inset 0 0.5px 1px rgba(255,255,255,0.08),
                                        inset 0.5px 0 1px rgba(255,255,255,0.04),
                                        inset 0 -0.5px 0.5px rgba(0,0,0,0.05),
                                    `
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <style>{`
            @keyframes liquidShine {
                0% {
                    transform: translateX(100%) translateY(-100%);
                    opacity: 0;
                }
                10% {
                    opacity: 1;
                }
                90% {
                    opacity: 1;
                }
                100% {
                    transform: translateX(-100%) translateY(100%);
                    opacity: 0;
                }
            }
            
            @keyframes liquidPulse {
                0%, 100% {
                    opacity: 0.6;
                    transform: scale(1);
                }
                50% {
                    opacity: 1;
                    transform: scale(1.1);
                }
            }
            
            @keyframes breathe {
                0%, 100% {
                    transform: translateY(0px);
                }
                50% {
                    transform: translateY(-12px);
                }
            }
        `}</style>
        </>
    );
};
